import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import { ApprovalStatus, MemberDetail, MemberListItem, PaginatedResponse, UserRole } from "../../types/admin";

export type MemberListQuery = {
  role?: UserRole;
  approval_status?: ApprovalStatus;
  is_active?: boolean;
  q?: string;
  page?: number;
  page_size?: number;
};

export type UpdateMemberPayload = {
  userId: number;
  user_name?: string;
  phone_number?: string;
  approval_status?: ApprovalStatus;
  is_active?: boolean;
};

type MembersState = {
  list: MemberListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  loadingList: boolean;
  listError: string | null;
  detail: MemberDetail | null;
  loadingDetail: boolean;
  detailError: string | null;
  updating: boolean;
  updateError: string | null;
  deleting: boolean;
  deleteError: string | null;
};

const initialState: MembersState = {
  list: [],
  totalCount: 0,
  page: 1,
  pageSize: 20,
  loadingList: false,
  listError: null,
  detail: null,
  loadingDetail: false,
  detailError: null,
  updating: false,
  updateError: null,
  deleting: false,
  deleteError: null,
};

const toQuery = (query: MemberListQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const fetchMembers = createAsyncThunk<
  PaginatedResponse<MemberListItem>,
  MemberListQuery,
  { rejectValue: string }
>("members/fetchMembers", async (query, { rejectWithValue }) => {
  try {
    return await apiRequest<PaginatedResponse<MemberListItem>>(`/admin/members${toQuery(query)}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : "회원 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const fetchMemberDetail = createAsyncThunk<MemberDetail, number, { rejectValue: string }>(
  "members/fetchMemberDetail",
  async (userId, { rejectWithValue }) => {
    try {
      return await apiRequest<MemberDetail>(`/admin/members/${userId}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "회원 상세 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const updateMember = createAsyncThunk<MemberDetail, UpdateMemberPayload, { rejectValue: string }>(
  "members/updateMember",
  async (payload, { rejectWithValue }) => {
    try {
      const { userId, ...body } = payload;
      return await apiRequest<MemberDetail>(`/admin/members/${userId}`, {
        method: "PATCH",
        body,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "회원 정보 수정에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const deleteMember = createAsyncThunk<{ deleted: boolean; user_id: number }, number, { rejectValue: string }>(
  "members/deleteMember",
  async (userId, { rejectWithValue }) => {
    try {
      return await apiRequest<{ deleted: boolean; user_id: number }>(`/admin/members/${userId}`, {
        method: "DELETE",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "회원 삭제에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {
    /** 지점/사업주 인증 상태 변경 후 목록을 다시 불러오도록 캐시 무효화 */
    invalidateMembersList(state) {
      state.list = [];
      state.totalCount = 0;
      state.listError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMembers.pending, (state) => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(fetchMembers.fulfilled, (state, action) => {
        state.loadingList = false;
        state.list = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
      })
      .addCase(fetchMembers.rejected, (state, action) => {
        state.loadingList = false;
        state.listError = action.payload ?? "회원 목록 조회에 실패했습니다.";
      })
      .addCase(fetchMemberDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchMemberDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.detail = action.payload;
      })
      .addCase(fetchMemberDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError = action.payload ?? "회원 상세 조회에 실패했습니다.";
      })
      .addCase(updateMember.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateMember.fulfilled, (state, action) => {
        state.updating = false;
        state.detail = action.payload;
      })
      .addCase(updateMember.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload ?? "회원 정보 수정에 실패했습니다.";
      })
      .addCase(deleteMember.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteMember.fulfilled, (state) => {
        state.deleting = false;
        // Detail can be cleared or keep as is, but we usually navigate away.
      })
      .addCase(deleteMember.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload ?? "회원 삭제에 실패했습니다.";
      });
  },
});

export const { invalidateMembersList } = membersSlice.actions;
export default membersSlice.reducer;
